import {
  type InfiniteData,
  type QueryFunction,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryOptions,
  useQuery,
} from '@tanstack/react-query'
import { useMemo } from 'react'
import { useMangoApi } from '~/atoms/MangoApi'
import { useUserState } from '~/atoms/UserState'
import { Sentry } from '~/services/sentry'
import { getPaginatedResultParts } from '~/utils/apiQueryUtils'

import { ApiError, PaginatedApiResult, PhoneNumber } from '@mangovoice/types'

type UseGetPhoneNumbersParams = {
  search?: string
  options?: Omit<
    UseInfiniteQueryOptions<PaginatedApiResult<PhoneNumber>, ApiError>,
    'queryKey'
  >
}

const pageSize = 50

/**
 * Custom React hook to fetch all phone numbers associated with the current PBX ID.
 * This hook retrieves the entire dataset without pagination by querying the API in a loop until all pages are fetched.
 *
 * **Note:** This hook is intended to be a temporary shim until server-side search is supported.
 * It provides a stopgap solution to enable searching across all phone numbers in the dataset.
 * Once server-side search is implemented, this hook should be deprecated and removed.
 *
 * @returns The query object provided by `useQuery`, extended with:
 *   - `results` - An array of all phone numbers retrieved from the API.
 */
export const useGetAllPhoneNumbers = () => {
  const { pbxId } = useUserState()
  const mangoApi = useMangoApi()
  const queryKey = useMemo(() => {
    const key = ['allPhoneNumbers', pbxId]
    return key
  }, [pbxId])

  const queryFn: QueryFunction<PhoneNumber[], QueryKey> = async () => {
    if (pbxId === 0) {
      return []
    }

    let allResults: PhoneNumber[] = []
    let offset = 0

    /** An arbitrarily high page limit as a hard-stop to infinite looping */
    const pageLimit = pageSize * 1000
    /** ES Lint rules don't allow for while(true) loops */
    let shouldQueryNextPage = true
    while (shouldQueryNextPage) {
      if (offset > pageLimit) {
        // If we get to this point, it's likely the user wouldn't notice anything is wrong
        // So capture this exception quietly.
        Sentry.captureException(
          new Error(
            `⭐️ LimitError: useGetAllPhoneNumbers exceeded page limit [${pageLimit}] and broke to prevent an infinite loop.`,
          ),
        )

        break
      }

      const { ok, data } = await mangoApi.getPhoneNumbers({
        pbx_id: pbxId,
        limit: pageSize,
        offset,
      })

      if (!ok || !data) {
        throw data as ApiError
      }

      allResults = [...allResults, ...data.results]

      if (!data.next) {
        shouldQueryNextPage = false
      }

      offset += pageSize
    }

    return allResults
  }

  const query = useQuery({
    queryKey,
    queryFn,
    retry: false,
  })

  return {
    ...query,
    results: query.data ?? [],
  }
}

export const useGetPhoneNumbers = ({
  options,
  search,
}: UseGetPhoneNumbersParams = {}) => {
  const { pbxId } = useUserState()
  const mangoApi = useMangoApi()
  const queryKey = useMemo(() => {
    const key = ['phoneNumbers', pbxId, search]
    return key
  }, [pbxId, search])

  const queryFn: QueryFunction<
    PaginatedApiResult<PhoneNumber>,
    QueryKey,
    unknown
  > = async ({ pageParam = 0 }) => {
    const emptyResult = { count: 0, next: null, previous: null, results: [] }
    if (pbxId === 0) {
      return Promise.resolve(emptyResult)
    }
    const offset = (pageParam ?? 0) as number
    const { ok, data } = await mangoApi.getPhoneNumbers({
      pbx_id: pbxId,
      limit: pageSize,
      offset,
      search,
    })

    if (!ok || !data) {
      return Promise.reject(data as ApiError)
    }

    return data
  }

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    ...options,
    initialPageParam: 0,
    retry: (options?.retry as number | boolean | undefined) ?? false,
    getNextPageParam: (lastPage) => {
      if (lastPage.next === null) {
        return null
      }
      return getPaginatedResultParts(lastPage)?.nextPageOffset
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.previous === null) {
        return null
      }
      return getPaginatedResultParts(firstPage)?.previousPageOffset
    },
  })

  const data = query.data as unknown as InfiniteData<
    PaginatedApiResult<PhoneNumber>,
    unknown
  >

  return {
    ...query,
    results: data?.pages.flatMap((page) => page.results) ?? [],
  }
}

export const useGetPhoneNumber = ({
  phoneNumberId,
}: {
  phoneNumberId: number
}) => {
  const mangoApi = useMangoApi()
  const query = useQuery<PhoneNumber | undefined, ApiError>({
    queryKey: ['phoneNumber', phoneNumberId],
    queryFn: async () => {
      if (phoneNumberId === 0) return Promise.reject(undefined)
      const { ok, data } = await mangoApi.getPhoneNumber({ phoneNumberId })

      if (!ok) {
        return Promise.reject(data as ApiError)
      }
      const phoneNumberData = data as PhoneNumber
      return phoneNumberData
    },
  })

  return { ...query }
}
